// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selection Comparison Component

import { useState, useMemo } from 'react';
import { EnhancedSelection, SelectionTag as SelectionTagType } from '@/types/selection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { TraitRadarChart } from './TraitRadarChart';
import { SelectionTag, SelectionTagList } from './SelectionTag';
import { SelectionBadge } from './SelectionBadge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { X, ChevronLeft, ChevronRight, Download, ArrowUpDown, Maximize2, MinusCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SelectionComparisonProps {
  selections: EnhancedSelection[];
  onClose: () => void;
  onRemoveSelection?: (selection: EnhancedSelection) => void;
  onViewDetails?: (selection: EnhancedSelection) => void;
}

export function SelectionComparison({
  selections,
  onClose,
  onRemoveSelection,
  onViewDetails
}: SelectionComparisonProps) {
  const [activeTab, setActiveTab] = useState('metrics');
  const [chartType, setChartType] = useState<'radar' | 'bar'>('radar');
  const [sortBy, setSortBy] = useState<string>('plantId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Sort selections
  const sortedSelections = useMemo(() => {
    return [...selections].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      // Handle different sort fields
      switch (sortBy) {
        case 'plantId':
          aValue = a.plantId;
          bValue = b.plantId;
          break;
        case 'generation':
          aValue = a.generation;
          bValue = b.generation;
          break;
        case 'date':
          aValue = new Date(a.selectionDate).getTime();
          bValue = new Date(b.selectionDate).getTime();
          break;
        case 'structure':
          aValue = a.growthMetrics.structure;
          bValue = b.growthMetrics.structure;
          break;
        case 'vigor':
          aValue = a.growthMetrics.vigor;
          bValue = b.growthMetrics.vigor;
          break;
        case 'yield':
          aValue = a.growthMetrics.yield;
          bValue = b.growthMetrics.yield;
          break;
        case 'frost':
          aValue = a.growthMetrics.frost;
          bValue = b.growthMetrics.frost;
          break;
        case 'overall':
          aValue = a.growthMetrics.overall;
          bValue = b.growthMetrics.overall;
          break;
        case 'aroma':
          aValue = a.sensoryData?.aroma_quality || 0;
          bValue = b.sensoryData?.aroma_quality || 0;
          break;
        case 'flavor':
          aValue = a.sensoryData?.flavor_quality || 0;
          bValue = b.sensoryData?.flavor_quality || 0;
          break;
        default:
          aValue = a.plantId;
          bValue = b.plantId;
      }
      
      // Apply sort direction
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [selections, sortBy, sortDirection]);
  
  // Toggle sort direction
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  
  // Render sort indicator
  const renderSortIndicator = (field: string) => {
    if (sortBy !== field) return null;
    
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };
  
  // Find common and unique tags
  const { commonTags, uniqueTags } = useMemo(() => {
    if (selections.length === 0) return { commonTags: [], uniqueTags: [] };
    
    // Start with first selection's tags
    const allTagNames = new Set<string>();
    const commonTagNames = new Set(selections[0].tags.map(tag => tag.name));
    const uniqueTagsMap = new Map<string, SelectionTagType[]>();
    
    // Compare with other selections
    selections.forEach(selection => {
      // Track all tag names
      selection.tags.forEach(tag => {
        allTagNames.add(tag.name);
      });
      
      // For common tags, only keep those that exist in all selections
      const currentTagNames = new Set(selection.tags.map(tag => tag.name));
      commonTagNames.forEach(name => {
        if (!currentTagNames.has(name)) {
          commonTagNames.delete(name);
        }
      });
      
      // Track unique tags by selection
      selection.tags.forEach(tag => {
        if (!uniqueTagsMap.has(tag.name)) {
          uniqueTagsMap.set(tag.name, []);
        }
        uniqueTagsMap.get(tag.name)?.push(tag);
      });
    });
    
    // Now filter uniqueTags to exclude common tags
    commonTagNames.forEach(name => {
      uniqueTagsMap.delete(name);
    });
    
    // Convert to arrays
    const commonTags = Array.from(commonTagNames).map(name => {
      const tagInstances = selections.map(s => s.tags.find(t => t.name === name)).filter(Boolean) as SelectionTagType[];
      return tagInstances[0]; // Just need one instance for display
    });
    
    const uniqueTags = Array.from(uniqueTagsMap.entries()).map(([name, tags]) => {
      return {
        name,
        tags,
        selectionIds: tags.map(tag => {
          const selection = selections.find(s => s.tags.some(t => t.id === tag.id));
          return selection?.id;
        })
      };
    });
    
    return { commonTags, uniqueTags };
  }, [selections]);
  
  // Export comparison as CSV
  const exportAsCSV = () => {
    // First, build headers
    const headers = [
      'Plant ID',
      'Generation',
      'Selection Date',
      'Structure',
      'Vigor',
      'Yield',
      'Frost',
      'Overall',
      'Aroma Intensity',
      'Aroma Quality',
      'Flavor Intensity',
      'Flavor Quality',
      'Smoke Harshness',
      'Ash Color',
      'Tags',
      'Rationale',
      'Notes'
    ];
    
    // Then build rows
    const rows = selections.map(selection => [
      selection.plantId,
      selection.generation,
      new Date(selection.selectionDate).toLocaleDateString(),
      selection.growthMetrics.structure,
      selection.growthMetrics.vigor,
      selection.growthMetrics.yield,
      selection.growthMetrics.frost,
      selection.growthMetrics.overall,
      selection.sensoryData?.aroma_intensity || '',
      selection.sensoryData?.aroma_quality || '',
      selection.sensoryData?.flavor_intensity || '',
      selection.sensoryData?.flavor_quality || '',
      selection.sensoryData?.smoke_harshness || '',
      selection.sensoryData?.ash_color || '',
      selection.tags.map(tag => tag.name).join(', '),
      selection.selectionRationale || '',
      selection.selectionNotes || ''
    ]);
    
    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `selection-comparison-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="flex flex-col h-full max-h-full overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="text-xl font-semibold">
          Comparing {selections.length} Selections
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportAsCSV}>
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 border-b">
          <TabsList className="my-2">
            <TabsTrigger value="metrics">Growth Metrics</TabsTrigger>
            <TabsTrigger value="sensory">Sensory Profile</TabsTrigger>
            <TabsTrigger value="tags">Tags & Badges</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>
        </div>
        
        {/* Metrics Tab */}
        <TabsContent value="metrics" className="flex-1 overflow-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-medium">Growth Metrics Comparison</div>
            <div className="flex gap-2">
              <Select value={chartType} onValueChange={(value: 'radar' | 'bar') => setChartType(value)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Chart Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="radar">Radar Chart</SelectItem>
                  <SelectItem value="bar">Bar Chart</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Radar Chart Visualization */}
          {chartType === 'radar' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">All Metrics Comparison</CardTitle>
                </CardHeader>
                <CardContent className="flex justify-center pt-0">
                  <div style={{ width: '100%', height: '400px' }}>
                    <TraitRadarChart
                      selection={sortedSelections[0]}
                      compareTo={sortedSelections.length > 1 ? sortedSelections[1] : undefined}
                      width={400}
                      height={400}
                      showLabels={true}
                      showLegend={true}
                    />
                  </div>
                </CardContent>
              </Card>
              
              <div className="space-y-6">
                {/* Metrics Table */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Metrics Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[30%]">
                            <button
                              className="flex items-center"
                              onClick={() => toggleSort('plantId')}
                            >
                              Plant ID {renderSortIndicator('plantId')}
                            </button>
                          </TableHead>
                          {['Structure', 'Vigor', 'Yield', 'Frost', 'Overall'].map(metric => (
                            <TableHead key={metric} className="text-center">
                              <button
                                className="flex items-center"
                                onClick={() => toggleSort(metric.toLowerCase())}
                              >
                                {metric} {renderSortIndicator(metric.toLowerCase())}
                              </button>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedSelections.map(selection => (
                          <TableRow key={selection.id}>
                            <TableCell className="font-medium">{selection.plantId}</TableCell>
                            <TableCell className="text-center">{selection.growthMetrics.structure}</TableCell>
                            <TableCell className="text-center">{selection.growthMetrics.vigor}</TableCell>
                            <TableCell className="text-center">{selection.growthMetrics.yield}</TableCell>
                            <TableCell className="text-center">{selection.growthMetrics.frost}</TableCell>
                            <TableCell className="text-center font-medium">{selection.growthMetrics.overall}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                
                {/* Statistics Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Statistics Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Metric</TableHead>
                          <TableHead className="text-center">Average</TableHead>
                          <TableHead className="text-center">Min</TableHead>
                          <TableHead className="text-center">Max</TableHead>
                          <TableHead className="text-center">Range</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {['Structure', 'Vigor', 'Yield', 'Frost', 'Overall'].map(metric => {
                          const key = metric.toLowerCase() as keyof EnhancedSelection['growthMetrics'];
                          const values = sortedSelections.map(s => s.growthMetrics[key]);
                          const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
                          const min = Math.min(...values);
                          const max = Math.max(...values);
                          
                          return (
                            <TableRow key={metric}>
                              <TableCell>{metric}</TableCell>
                              <TableCell className="text-center">{avg.toFixed(1)}</TableCell>
                              <TableCell className="text-center">{min}</TableCell>
                              <TableCell className="text-center">{max}</TableCell>
                              <TableCell className="text-center">{(max - min).toFixed(1)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          
          {/* Bar Chart Visualization (placeholder) */}
          {chartType === 'bar' && (
            <div className="text-center p-8 border rounded-lg bg-gray-50">
              <p className="text-gray-500">
                Bar chart visualization would be implemented here.
              </p>
            </div>
          )}
        </TabsContent>
        
        {/* Sensory Tab */}
        <TabsContent value="sensory" className="flex-1 overflow-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-medium">Sensory Profile Comparison</div>
          </div>
          
          {/* Check if sensory data is available */}
          {sortedSelections.every(s => !s.sensoryData) ? (
            <div className="text-center p-8 border rounded-lg bg-gray-50">
              <p className="text-gray-500">
                No sensory data available for these selections.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Sensory Metrics Comparison</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30%]">
                          <button
                            className="flex items-center"
                            onClick={() => toggleSort('plantId')}
                          >
                            Plant ID {renderSortIndicator('plantId')}
                          </button>
                        </TableHead>
                        <TableHead className="text-center">
                          <button
                            className="flex items-center"
                            onClick={() => toggleSort('aroma')}
                          >
                            Aroma Quality {renderSortIndicator('aroma')}
                          </button>
                        </TableHead>
                        <TableHead className="text-center">
                          <button
                            className="flex items-center"
                            onClick={() => toggleSort('flavor')}
                          >
                            Flavor Quality {renderSortIndicator('flavor')}
                          </button>
                        </TableHead>
                        <TableHead className="text-center">Smoke Harshness</TableHead>
                        <TableHead className="text-center">Ash Color</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedSelections.map(selection => (
                        <TableRow key={selection.id}>
                          <TableCell className="font-medium">{selection.plantId}</TableCell>
                          <TableCell className="text-center">{selection.sensoryData?.aroma_quality || '-'}</TableCell>
                          <TableCell className="text-center">{selection.sensoryData?.flavor_quality || '-'}</TableCell>
                          <TableCell className="text-center">{selection.sensoryData?.smoke_harshness || '-'}</TableCell>
                          <TableCell className="text-center">
                            {selection.sensoryData?.ash_color ? (
                              <div className="flex items-center justify-center">
                                <div
                                  className="h-4 w-4 rounded-full border mr-2"
                                  style={{
                                    backgroundColor: selection.sensoryData.ash_color === 'white' ? '#FFFFFF' :
                                                     selection.sensoryData.ash_color === 'light-gray' ? '#D3D3D3' :
                                                     selection.sensoryData.ash_color === 'medium-gray' ? '#808080' :
                                                     selection.sensoryData.ash_color === 'dark-gray' ? '#404040' : '#000000',
                                    borderColor: selection.sensoryData.ash_color === 'white' ? '#D1D5DB' : 'transparent'
                                  }}
                                />
                                {selection.sensoryData.ash_color.replace('-', ' ')}
                              </div>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Aroma Profiles</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {sortedSelections.map(selection => (
                      <div key={selection.id} className="space-y-2">
                        <Label className="text-sm font-medium">{selection.plantId}</Label>
                        <div className="flex flex-wrap gap-2">
                          {selection.sensoryData?.aroma_profiles?.length ? (
                            selection.sensoryData.aroma_profiles.map(profile => (
                              <Badge key={profile} variant="secondary" className="bg-purple-50 text-purple-800">
                                {profile}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500 italic">No aroma profiles specified</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Flavor Profiles</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {sortedSelections.map(selection => (
                      <div key={selection.id} className="space-y-2">
                        <Label className="text-sm font-medium">{selection.plantId}</Label>
                        <div className="flex flex-wrap gap-2">
                          {selection.sensoryData?.flavor_profiles?.length ? (
                            selection.sensoryData.flavor_profiles.map(profile => (
                              <Badge key={profile} variant="secondary" className="bg-indigo-50 text-indigo-800">
                                {profile}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500 italic">No flavor profiles specified</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Common Profiles</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Common Aroma Profiles</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sortedSelections.length > 1 ? (
                          getCommonProfiles(sortedSelections, 'aroma').map(profile => (
                            <Badge key={profile} variant="outline" className="bg-purple-100 text-purple-800">
                              {profile}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500 italic">Need more selections to compare</span>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Common Flavor Profiles</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sortedSelections.length > 1 ? (
                          getCommonProfiles(sortedSelections, 'flavor').map(profile => (
                            <Badge key={profile} variant="outline" className="bg-indigo-100 text-indigo-800">
                              {profile}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500 italic">Need more selections to compare</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        
        {/* Tags Tab */}
        <TabsContent value="tags" className="flex-1 overflow-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Common Tags</CardTitle>
                <CardDescription>Tags that appear in all selections</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {commonTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {commonTags.map(tag => (
                      <SelectionTag key={tag.id} tag={tag} />
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-md">
                    No common tags found among these selections.
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Unique Tags</CardTitle>
                <CardDescription>Tags that appear in some but not all selections</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {uniqueTags.length > 0 ? (
                  <div className="space-y-4">
                    {uniqueTags.map(({ name, tags, selectionIds }) => (
                      <div key={name} className="space-y-1">
                        <SelectionTag tag={tags[0]} />
                        <div className="text-xs text-gray-500 ml-2">
                          Found in: {selectionIds.filter(Boolean).length} of {selections.length} selections
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-md">
                    No unique tags found among these selections.
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Badges Comparison</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  {sortedSelections.map(selection => (
                    <div key={selection.id} className="space-y-2">
                      <Label className="text-sm font-medium">{selection.plantId}</Label>
                      <div className="flex flex-wrap gap-2">
                        {selection.badges.length > 0 ? (
                          selection.badges.map(badge => (
                            <SelectionBadge key={badge.id} badge={badge} />
                          ))
                        ) : (
                          <span className="text-sm text-gray-500 italic">No badges</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Tag Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  {sortedSelections.map(selection => (
                    <div key={selection.id} className="space-y-2">
                      <Label className="text-sm font-medium">{selection.plantId}</Label>
                      <div className="flex flex-wrap gap-2">
                        {selection.tags.length > 0 ? (
                          <SelectionTagList tags={selection.tags} />
                        ) : (
                          <span className="text-sm text-gray-500 italic">No tags</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Details Tab */}
        <TabsContent value="details" className="flex-1 overflow-auto p-4">
          <div className="space-y-6">
            {sortedSelections.map(selection => (
              <Card key={selection.id} className="overflow-hidden">
                <CardHeader className="bg-gray-50 pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{selection.plantId}</CardTitle>
                      <CardDescription>
                        {selection.generation} • Selected on {new Date(selection.selectionDate).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <div className="flex gap-1">
                      {onViewDetails && (
                        <Button variant="ghost" size="sm" onClick={() => onViewDetails(selection)}>
                          <Maximize2 className="h-4 w-4 mr-1" />
                          Details
                        </Button>
                      )}
                      {onRemoveSelection && (
                        <Button variant="ghost" size="sm" onClick={() => onRemoveSelection(selection)}>
                          <MinusCircle className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {/* Selection Rationale */}
                    {selection.selectionRationale && (
                      <div>
                        <Label className="text-sm font-medium">Selection Rationale</Label>
                        <p className="mt-1 text-sm">{selection.selectionRationale}</p>
                      </div>
                    )}
                    
                    {/* Selection Highlights */}
                    {selection.selectionHighlights && selection.selectionHighlights.length > 0 && (
                      <div>
                        <Label className="text-sm font-medium">Highlights</Label>
                        <ul className="mt-1 text-sm space-y-1 pl-5 list-disc">
                          {selection.selectionHighlights.map((highlight, index) => (
                            <li key={index}>{highlight}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Selection Notes */}
                    {selection.selectionNotes && (
                      <div>
                        <Label className="text-sm font-medium">Notes</Label>
                        <p className="mt-1 text-sm">{selection.selectionNotes}</p>
                      </div>
                    )}
                    
                    {/* No data placeholder */}
                    {!selection.selectionRationale && !selection.selectionNotes && 
                      (!selection.selectionHighlights || selection.selectionHighlights.length === 0) && (
                      <div className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-md">
                        No additional details provided for this selection.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Helper function to find common profiles across selections
function getCommonProfiles(selections: EnhancedSelection[], type: 'aroma' | 'flavor'): string[] {
  if (selections.length === 0) return [];
  
  const key = `${type}_profiles` as keyof EnhancedSelection['sensoryData'];
  
  // Start with all profiles from first selection
  const firstSelectionProfiles = selections[0].sensoryData?.[key] as string[] || [];
  if (firstSelectionProfiles.length === 0) return [];
  
  // Initialize with all profiles from first selection
  const commonProfiles = new Set(firstSelectionProfiles);
  
  // Keep only profiles that exist in all selections
  selections.slice(1).forEach(selection => {
    const profiles = selection.sensoryData?.[key] as string[] || [];
    const profilesSet = new Set(profiles);
    
    // Keep only common profiles
    Array.from(commonProfiles).forEach(profile => {
      if (!profilesSet.has(profile)) {
        commonProfiles.delete(profile);
      }
    });
  });
  
  return Array.from(commonProfiles);
} 